import { useEffect, useState } from "react";
import { openPrivate, roundForTime, sealPrivate, type Event, type RandomnessBeacon } from "@timecapsule/crypto";
import { api } from "../api";
import { link } from "../App";
import { estimateStamps, knownShortage } from "../billing";
import { chainClient, explain } from "../capsules";
import { saveCapsuleEvent } from "../download";
import { useT } from "../i18n";
import { isKeyRequestCancelled, useSession } from "../session";
import { sentPrivate } from "../storage";
import { formatCountdown, formatTime, useNow } from "../time";

/** About ten minutes: short enough to wait for, long enough to see the lock hold (reviews/06-action-plan.md 4.1). */
export const TRY_MINUTES = 10;
const STORED = "vrelis-try";

/** The capsule of this run, kept in the tab so a reload during the wait does not lose the screen. */
function loadStored(memberId: number): Event | null {
  try {
    const raw = sessionStorage.getItem(STORED);
    const parsed = raw ? (JSON.parse(raw) as { memberId: number; event: Event }) : null;
    return parsed && parsed.memberId === memberId ? parsed.event : null;
  } catch {
    return null;
  }
}

function storeEvent(memberId: number, event: Event | null) {
  try {
    if (event) sessionStorage.setItem(STORED, JSON.stringify({ memberId, event }));
    else sessionStorage.removeItem(STORED);
  } catch {
    // A private window may refuse storage: the run still works, only a reload forgets it.
  }
}

/**
 * Try it: one guided capsule for a newcomer (reviews/06-action-plan.md section 4, owner 2026-10-11).
 * Not the ordinary Compose: the recipient is you, the opening is about ten minutes away, and the
 * page stays with you through the wait, the phrase, the downloaded copy and the opening.
 * `?seconds=` shortens the wait (a member can choose five minutes in Compose anyway); tests use it.
 */
export function TryIt() {
  const t = useT();
  const tt = t.tryIt;
  const { me, withKey, refresh } = useSession();
  const now = useNow(1000);
  const [subject, setSubject] = useState(tt.subjectSuggestion);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [event, setEvent] = useState<Event | null>(() => loadStored(me.id));
  const [opened, setOpened] = useState<string | null>(null);
  const [beacon, setBeacon] = useState<RandomnessBeacon | null>(null);

  const seconds = (() => {
    const asked = Number(new URLSearchParams(location.search).get("seconds"));
    return Number.isFinite(asked) && asked >= 10 && asked <= TRY_MINUTES * 60 ? asked : TRY_MINUTES * 60;
  })();
  const balance = me.billing ? me.billing.stamps.remaining : null;
  const cost = estimateStamps({ wraps: 1, envelope: false });
  const shortage = knownShortage(cost, balance);
  // The wrap's "unlock" tag is the opening time in seconds (the server sees it too; SPEC.md section 9).
  const unlockSeconds = event ? Number(event.tags.find((tag) => tag[0] === "unlock")?.[1] ?? 0) : 0;
  const unlockAt = unlockSeconds > 0 ? unlockSeconds * 1000 : null;

  useEffect(() => storeEvent(me.id, event), [event, me.id]);

  async function seal() {
    if (!subject.trim() || !text.trim() || busy || shortage) return;
    setBusy(true);
    setError(null);
    try {
      const finalRound = roundForTime(Date.now() + seconds * 1000);
      const profile = me.profile;
      const sender = profile ? { display_name: profile.display_name, name: profile.name ?? undefined, about: profile.about ?? undefined } : undefined;
      const sealed = await withKey(t.keyDialog.purposes.send, (secretKey) =>
        sealPrivate({ subject: subject.trim(), plaintext: text, files: [], round: finalRound, secretKey, chainClient: chainClient(), sender, recipientPublicKey: me.pubkey! }),
      );
      await api.submit(sealed);
      await sentPrivate.save(me.id, sealed.id, { recipient: me.pubkey!, round: finalRound, createdAt: Math.floor(Date.now() / 1000) });
      setEvent(sealed);
      void refresh().catch(() => undefined);
    } catch (err) {
      if (!isKeyRequestCancelled(err)) setError(explain(err));
    }
    setBusy(false);
  }

  async function open() {
    if (!event) return;
    setBusy(true);
    setError(null);
    try {
      const result = await withKey(t.keyDialog.purposes.open, (secretKey) => openPrivate(event, secretKey, chainClient()));
      setOpened(result.plaintext);
      // The copy downloaded from now on carries the signal, so it opens with no network at all.
      if (unlockAt !== null) setBeacon(await chainClient().get(roundForTime(unlockAt)).catch(() => null));
    } catch (err) {
      if (!isKeyRequestCancelled(err)) setError(explain(err));
    }
    setBusy(false);
  }

  function again() {
    setEvent(null);
    setOpened(null);
    setBeacon(null);
    setText("");
    setSubject(tt.subjectSuggestion);
  }

  return (
    <div className="try-page compose-page">
      <div className="page-head">
        <h1>{tt.title}</h1>
        <p className="muted small">{tt.lead(TRY_MINUTES)}</p>
      </div>
      {error && <p className="error" role="alert">{error}</p>}
      {!me.pubkey && <p className="note"><a href="/keys" onClick={link("/keys")}>{t.account.notSetUp}</a></p>}

      {!event && me.pubkey && (
        <section className="card stack">
          <label>
            {t.compose.subject}
            <input value={subject} maxLength={200} onChange={(e) => setSubject(e.target.value)} dir="auto" />
          </label>
          <p className="small muted">{t.design.subjectHint}</p>
          <label>
            {tt.textLabel}
            <textarea rows={5} value={text} onChange={(e) => setText(e.target.value)} placeholder={tt.textPlaceholder} dir="auto" />
          </label>
          <p className="small muted">{tt.toYou(Math.round(seconds / 60) || 1)}</p>
          {balance !== null && <p className={shortage ? "error small" : "small muted"}>{shortage ? t.compose.stampShort(cost, balance) : t.compose.stampStatus(cost, balance)}</p>}
          <div className="chips">
            <button className="primary" disabled={busy || shortage || !subject.trim() || !text.trim()} onClick={() => void seal()}>{tt.seal}</button>
            <a className="button" href="/compose" onClick={link("/compose")}>{tt.skip}</a>
          </div>
        </section>
      )}

      {event && unlockAt !== null && (
        <section className="card stack try-after" aria-live="polite">
          <h2>{opened !== null ? tt.openedTitle : now < unlockAt ? tt.sealedTitle : tt.readyTitle}</h2>
          {opened === null && now < unlockAt && <p>{tt.opensIn(formatCountdown(unlockAt - now), formatTime(unlockAt))}</p>}
          {opened === null && now >= unlockAt && (
            <button className="primary" disabled={busy} onClick={() => void open()}>{tt.open}</button>
          )}
          {opened !== null && (
            <>
              <p className="plaintext" dir="auto">{opened}</p>
              <p>{tt.openedNote}</p>
              <div className="chips">
                <a className="button primary" href="/compose" onClick={link("/compose")}>{tt.writeReal}</a>
                <button onClick={again}>{tt.again}</button>
              </div>
            </>
          )}
          <h3>{tt.phraseTitle}</h3>
          <p className="small">{tt.phraseText}</p>
          <h3>{tt.copyTitle}</h3>
          <p className="small">{tt.copyText}</p>
          <div className="chips">
            <button onClick={() => saveCapsuleEvent(event, beacon)}>{tt.download}</button>
            <a className="button" href="/vrelis-opener.html" download>{tt.opener}</a>
          </div>
        </section>
      )}
    </div>
  );
}
